// ============================================================
// CineSync — Invitation & Admin Client Service
// Communicates with backend endpoints to:
//   - Validate invitation tokens
//   - Redeem invitations atomically after auth
//   - Fetch host approval & admin status
//   - Manage invitations (Admin only)
// ============================================================

import { auth } from '@/lib/firebase';
import { getApiBaseUrl } from '@/lib/apiConfig';

async function safeJsonParse(res: Response): Promise<any> {
  const text = await res.text();
  if (!text || !text.trim()) return {};
  if (text.trim().startsWith('<')) {
    throw new Error('Received unexpected HTML response from server. Check that backend server is running.');
  }
  try {
    return JSON.parse(text);
  } catch (err: any) {
    throw new Error(`Invalid server response: ${err.message}`);
  }
}

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export interface ValidateResult {
  valid: boolean;
  expiresAt?: number | null;
  error?: string;
}

export interface RedeemResult {
  success: boolean;
  isApprovedHost: boolean;
  error?: string;
}

export interface ProfileStatusResult {
  uid: string;
  email: string | null;
  displayName: string | null;
  isApprovedHost: boolean;
  isAdmin: boolean;
}

export interface AdminInvitationItem {
  tokenHash: string;
  status: 'active' | 'used' | 'revoked' | 'expired';
  createdAt: number;
  expiresAt: number | null;
  createdByUid: string;
  usedByUid?: string | null;
  usedByEmail?: string | null;
  usedAt?: number | null;
  revokedAt?: number | null;
}

/**
 * Validates an invitation token against the backend.
 * Public endpoint — requires no authentication.
 */
export async function validateInvitation(token: string): Promise<ValidateResult> {
  if (!token || !token.trim()) {
    return { valid: false, error: 'No invitation token provided.' };
  }

  const baseUrl = getApiBaseUrl();
  try {
    const res = await fetch(`${baseUrl}/api/invitations/validate?token=${encodeURIComponent(token.trim())}`);
    const data = await safeJsonParse(res);
    return data;
  } catch (err: any) {
    return { valid: false, error: err.message || 'Failed to connect to validation server.' };
  }
}

/**
 * Redeems an invitation token for the currently authenticated user.
 * Grants host approval on the backend.
 */
export async function redeemInvitation(token: string): Promise<RedeemResult> {
  const baseUrl = getApiBaseUrl();
  const headers = await getAuthHeader();

  if (!headers.Authorization) {
    return { success: false, isApprovedHost: false, error: 'You must be signed in to redeem an invitation.' };
  }

  try {
    const res = await fetch(`${baseUrl}/api/invitations/redeem`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify({ token: token.trim() }),
    });

    const data = await safeJsonParse(res);
    return data;
  } catch (err: any) {
    return { success: false, isApprovedHost: false, error: err.message || 'Failed to redeem invitation.' };
  }
}

/**
 * Fetches the caller's host approval and admin status from the backend.
 */
export async function fetchProfileStatus(): Promise<ProfileStatusResult | null> {
  const baseUrl = getApiBaseUrl();
  const headers = await getAuthHeader();

  if (!headers.Authorization) return null;

  try {
    const res = await fetch(`${baseUrl}/api/auth/profile-status`, {
      method: 'GET',
      headers,
    });

    if (!res.ok) return null;
    return await safeJsonParse(res);
  } catch {
    return null;
  }
}

/**
 * (Admin Only) Fetches all invitations.
 */
export async function adminListInvitations(): Promise<AdminInvitationItem[]> {
  const baseUrl = getApiBaseUrl();
  const headers = await getAuthHeader();

  const res = await fetch(`${baseUrl}/api/admin/invitations`, {
    method: 'GET',
    headers,
  });

  if (!res.ok) {
    const err = await safeJsonParse(res).catch(() => ({}));
    throw new Error(err.error || `Failed to list invitations (${res.status}).`);
  }

  const data = await safeJsonParse(res);
  return data.invitations || [];
}

/**
 * (Admin Only) Creates a new invitation.
 * Returns the raw token to construct the invitation link.
 */
export async function adminCreateInvitation(
  expiresInHours?: number | null
): Promise<{ token: string; tokenHash: string; expiresAt: number | null }> {
  const baseUrl = getApiBaseUrl();
  const headers = await getAuthHeader();

  const res = await fetch(`${baseUrl}/api/admin/invitations/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify({ expiresInHours }),
  });

  if (!res.ok) {
    const err = await safeJsonParse(res).catch(() => ({}));
    throw new Error(err.error || `Failed to create invitation (${res.status}).`);
  }

  const data = await safeJsonParse(res);
  return {
    token: data.token,
    tokenHash: data.tokenHash,
    expiresAt: data.expiresAt,
  };
}

/**
 * (Admin Only) Revokes an invitation link.
 */
export async function adminRevokeInvitation(tokenHash: string): Promise<boolean> {
  const baseUrl = getApiBaseUrl();
  const headers = await getAuthHeader();

  const res = await fetch(`${baseUrl}/api/admin/invitations/revoke`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify({ tokenHash }),
  });

  return res.ok;
}

/**
 * (Admin Only) Revokes a user's approved host status.
 */
export async function adminRevokeHostApproval(targetUid: string): Promise<boolean> {
  const baseUrl = getApiBaseUrl();
  const headers = await getAuthHeader();

  const res = await fetch(`${baseUrl}/api/admin/users/revoke-host`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify({ targetUid }),
  });

  return res.ok;
}

